import * as React from 'react';
import {
  MdAdd,
  MdBlock,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdPauseCircle,
  MdSchedule,
  MdSearch,
  MdVisibility,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from 'react-router-dom';
import { APPRoutes } from '../../Utils/Route';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { getAllHealthcarePayors, deleteHealthcarePayor } from '../../Services/ProviderService';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';

const STATUS_OPTIONS = ['All', 'Completed', 'Pending'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Completed: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Pending: { backgroundColor: 'var(--hc-status-pending-list-bg)', color: 'var(--hc-icon-amber-text)' },
};


export default function HealthcarePayorsList() {
  const navigate = useNavigate();

  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewPayor = permissionHas(userPermissionCodes, PermissionCodes.HEALTHCARE_PAYOR_VIEW, decodedToken);
  const canCreatePayor = permissionHas(userPermissionCodes, PermissionCodes.HEALTHCARE_PAYOR_CREATE, decodedToken);
  const canEditPayor = permissionHas(userPermissionCodes, PermissionCodes.HEALTHCARE_PAYOR_EDIT, decodedToken);
  const canDeletePayor = permissionHas(userPermissionCodes, PermissionCodes.HEALTHCARE_PAYOR_DELETE, decodedToken);
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');
  const [apiStats, setApiStats] = React.useState(null);

  const handleCreatePayor = React.useCallback(() => {
    navigate(APPRoutes.HEALTHCARE_PAYOR_FORM);
  }, [navigate]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }

    setDeleteTarget(null);
  }, [deleteLoading]);

  const loadPayors = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getAllHealthcarePayors(token);
      if (response?.is_success) {
        const payors = response?.data?.payors ?? [];
        const mapped = payors.map((p) => ({
          Id: p.id,
          HealthcarePayorName: p.payor_name ?? p.payorName ?? '-',
          ProviderName:
            p.provider_name ?? (p.provider ? `${p.provider.first_name || ''} ${p.provider.last_name || ''}`.trim() : ''),
          Status: p.payor_status_name ?? p.status_name ?? '',
          ManagedBy: p.handled_by_name ?? p.modified_by_name ?? '',
          Organization : p.organization_name ?? '',
          _raw: p,
        }));
        setRows(mapped);
        setApiStats(response?.data?.stats ?? null);
      } else {
        setRows([]);
        setApiStats(null);
      }
    } catch (error) {
      console.error('Failed to load healthcare payors:', error);
      setRows([]);
      setApiStats(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoading(true);
      setDeleteError('');
      const response = await deleteHealthcarePayor(token, deleteTarget.Id,);
      if (response?.is_success) {
        await loadPayors();
        toast.success(response?.message || 'Healthcare payor deleted successfully.');
        setDeleteTarget(null);
      } else if (response?.status_code === 500) {
        setDeleteError('Internal Server Error. Please contact support.');
      } else {
        setDeleteError(response?.message || 'Failed to delete healthcare payor');
      }
    } catch (error) {
      console.error('Error deleting healthcare payor:', error);
      setDeleteError(error?.message || 'Failed to delete healthcare payor');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadPayors]);

  React.useEffect(() => {
    loadPayors();
  }, [loadPayors]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const rowStatus = row.Status || '';
      const matchesStatus = status === 'All' || rowStatus === status;

      const matchesSearch =
        !term ||
        row.HealthcarePayorName?.toLowerCase().includes(term) ||
        row.ProviderName?.toLowerCase().includes(term) ||
        row.ManagedBy?.toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(() => {
    if (apiStats) {
      return [
        {
          label: 'Total Payors',
          value: apiStats.total_healthcare_payor ?? 0,
          icon: <MdCheckCircle size={22} color="var(--hc-icon-blue-text)" />,
          iconBg: 'var(--hc-icon-blue-bg)',
          accentClass: 'border-primary',
          valueClass: 'text-primary',
        },
        {
          label: 'Active Payors',
          value: apiStats.active_payor ?? 0,
          icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
          iconBg: 'var(--hc-icon-green-bg)',
          accentClass: 'border-success',
          valueClass: 'text-success',
        },
        {
          label: 'Pending Payors',
          value: apiStats.pending_payor ?? 0,
          icon: <MdSchedule size={22} color="var(--hc-icon-amber-text)" />,
          iconBg: 'var(--hc-icon-amber-bg)',
          accentClass: 'border-warning',
          valueClass: 'text-warning',
        },
      ];
    }

    return [
      {
        label: 'Total Payors',
        value: rows.length,
        icon: <MdCheckCircle size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Completed Payors',
        value: rows.filter((row) => row.Status === 'Completed').length,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Pending Payors',
        value: rows.filter((row) => row.Status === 'Pending').length,
        icon: <MdSchedule size={22} color="var(--hc-icon-amber-text)" />,
        iconBg: 'var(--hc-icon-amber-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
    ];
  }, [rows, apiStats]);

  const columns = React.useMemo(() => [
    {
      id: 'HealthcarePayorName',
      label: 'Healthcare Payor',
      minWidth: 180,
      format: (value, row) => canViewPayor ? (
        <button
          type="button"
          className="btn p-0 text-start text-dark border-0 bg-transparent"
          style={{ font: 'inherit' }}
          onClick={() => navigate(APPRoutes.HEALTHCARE_PAYOR_VIEW.replace(':id', row.Id), { state: { payor: row._raw || row } })}
        >
          {value || '-'}
        </button>
      ) : (
        <span className="text-dark">{value || '-'}</span>
      ),
    },
    {
      id: 'ProviderName',
      label: 'Provider',
      minWidth: 200,
      format: (value) => value || '-',
    },
    {
      id : 'Organization',
      label : 'Organization',
      minWidth : 200,
      format : (value) => value || '-',
    },
    {
      id: 'Status',
      label: 'Status',
      minWidth: 120,
      align: 'center',
      format: (value) => (
        <span
          className="badge rounded-pill fw-bolder px-3 py-2 text-center"
          style={{
            minWidth: '96px',
            fontWeight: 1000,
            ...(STATUS_BADGE_STYLE[value] || {
              backgroundColor: 'var(--hc-status-inactive-bg)',
              color: 'var(--hc-status-inactive-text)',
            }),
          }}
        >
          {value}
        </span>
      ),
    },
    {
      id: 'ManagedBy',
      label: 'Managed By',
      minWidth: 170,
      format: (value) => value || '-',
    },
    ...(canViewPayor || canEditPayor || canDeletePayor ? [{
      id: 'actions',
      label: 'Actions',
      minWidth: 90,
      align: 'center',
      format: (_value, row) => (
        <div className="d-flex align-items-center justify-content-center gap-2">
          {canViewPayor ? (
            <button
              type="button"
              title="View"
              className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
              onClick={() => navigate(APPRoutes.HEALTHCARE_PAYOR_VIEW.replace(':id', row.Id), { state: { payor: row._raw || row } })}
            >
              <MdVisibility size={16} />
            </button>
          ) : null}
          {canEditPayor ? (
            <button
              type="button"
              title="Edit"
              className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
              onClick={() => navigate(APPRoutes.HEALTHCARE_PAYOR_EDIT.replace(':id', row.Id), { state: { payor: row._raw || row } })}
            >
              <MdEdit size={16} />
            </button>
          ) : null}
          {canDeletePayor ? (
            <button
              type="button"
              title="Delete"
              className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
              onClick={() => handleDeleteClick(row)}
            >
              <MdDelete size={16} />
            </button>
          ) : null}
        </div>
      ),
    }] : []),
  ], [navigate, handleDeleteClick, canViewPayor, canEditPayor, canDeletePayor]);

  return (
    <>
      <PageTitle title="Healthcare Payor - Aegis 360"/>
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Healthcare Payors Management</h4>
          <p className="text-secondary mb-0 small">Track and manage healthcare payor relationships and credentialing progress.</p>
        </div>
        {canCreatePayor ? (
          <button
            onClick={handleCreatePayor}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Add HealthCare Payor</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: '60%' }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: '40%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
                <div className="d-inline-flex align-items-center justify-content-center rounded-3" style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}>
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>{stat.label}</span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>{stat.value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none" style={{ left: '12px' }}>
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search payor, provider or managed by"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">
              <SearchableDropdown
                placeholder="Select status"
                options={STATUS_DROPDOWN_OPTIONS}
                value={status}
                onChange={setStatus}
                showSearch={true}
              /> 

            {/* <FormControl fullWidth size="small">
              <InputLabel id="payor-status-label">Status</InputLabel>
              <Select
                labelId="payor-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '10%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Healthcare Payor"
        entityLabel="healthcare payor"
        itemName={deleteTarget?.HealthcarePayorName || ''}
        description="You are about to remove this healthcare payor from the platform. This action is permanent."
        confirmText="Delete Payor"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
